/******************************************************************************
 * Copyright (c) 2026 Contributors to the Eclipse Foundation.
 *
 * See the NOTICE file(s) distributed with this work for additional
 * information regarding copyright ownership.
 *
 * This program and the accompanying materials are made available under the
 * terms of the Eclipse Public License 2.0 which is available at
 * https://www.eclipse.org/legal/epl-2.0.
 *
 * SPDX-License-Identifier: EPL-2.0
 *****************************************************************************/

import { FunctionComponent, ReactNode, createContext, useContext } from 'react';
import { MainContext } from 'openvsx-webui';
import { useAnalyticsStatus } from './use-registry-analytics';

const AnalyticsAccessContext = createContext(false);

/**
 * Observes the analytics status around the account avatar, so the account menu's entry does not
 * query, and pop in, each time the menu opens. The desktop avatar is always mounted; the mobile
 * one lives in the hamburger menu, so there the first open still waits on the request.
 */
export const AnalyticsStatusProvider: FunctionComponent<{ children: ReactNode }> = ({ children }) => {
  const { user } = useContext(MainContext);
  const { data: status, isError: noAccess } = useAnalyticsStatus({ enabled: user !== undefined });
  // a failed refetch keeps the last successful payload; access goes with the error
  const hasAccess = status !== undefined && !noAccess;
  return <AnalyticsAccessContext.Provider value={hasAccess}>{children}</AnalyticsAccessContext.Provider>;
};

/** Whether the status check granted analytics access; false while it is still out. */
export const useHasAnalyticsAccess = (): boolean => useContext(AnalyticsAccessContext);
